import { ArrowRight, MapPin, Search, ShoppingBag, Store } from "lucide-react";
import { useMemo, useState } from "react";
import { useStorefront } from "../contexts/StorefrontContext";
import { buildTenantStoreUrl } from "../lib/storefrontUrl";
import type { StoreDirectoryEntry } from "../types/storefront";

function DirectoryStoreLogo({ store }: { store: StoreDirectoryEntry }) {
  const [failed, setFailed] = useState(false);
  if (!store.logo || failed) {
    return <span className="directory-store-placeholder" aria-hidden="true"><Store /></span>;
  }
  return <img src={store.logo} alt={`${store.storeName} logo`} onError={() => setFailed(true)} />;
}

export default function StoreDirectoryPage() {
  const { stores, loading, error, retry } = useStorefront();
  const [query, setQuery] = useState("");
  const visibleStores = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return stores;
    return stores.filter((store) => store.storeName.toLowerCase().includes(normalized));
  }, [query, stores]);

  const tenantUrl = (slug: string) => buildTenantStoreUrl(slug, {
    hostname: window.location.hostname,
    origin: window.location.origin,
    rootDomain: import.meta.env.VITE_ROOT_DOMAIN
  });

  return (
    <div className="store-directory">
      <header className="directory-header">
        <a className="directory-brand" href="/" aria-label="Elmor POS store directory">
          <img src="/favicon.svg" alt="" />
          <span><strong>ELMOR POS</strong><small>Store Directory</small></span>
        </a>
      </header>

      <main>
        <section className="directory-hero">
          <div className="directory-hero-inner">
            <div>
              <p className="directory-eyebrow">Shop from trusted local businesses</p>
              <h1>Choose a store and start shopping</h1>
              <p>Browse live catalogs from businesses powered by Elmor POS, then order directly from the store that serves you.</p>
            </div>
            <div className="directory-hero-mark" aria-hidden="true"><ShoppingBag /></div>
          </div>
        </section>

        <section className="directory-content" aria-labelledby="directory-title">
          <div className="directory-toolbar">
            <div>
              <p className="directory-eyebrow">Available storefronts</p>
              <h2 id="directory-title">Find your store</h2>
            </div>
            <label className="directory-search">
              <Search aria-hidden="true" />
              <span className="sr-only">Search stores</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by store name"
              />
            </label>
          </div>

          {loading && (
            <div className="directory-grid" aria-label="Loading stores" aria-busy="true">
              {Array.from({ length: 6 }, (_, index) => <div className="directory-card directory-card-skeleton" key={index} />)}
            </div>
          )}

          {!loading && error && (
            <div className="directory-state" role="alert">
              <Store />
              <h3>The store directory is temporarily unavailable</h3>
              <p>We could not reach the store service. Please check your connection and try again.</p>
              <button className="directory-primary-button" onClick={retry}>Try again</button>
            </div>
          )}

          {!loading && !error && visibleStores.length === 0 && (
            <div className="directory-state">
              <Search />
              <h3>{query ? "No stores match your search" : "No stores are available yet"}</h3>
              <p>{query ? "Try another store name." : "Please check back soon for active online stores."}</p>
              {query && <button className="directory-secondary-button" onClick={() => setQuery("")}>Clear search</button>}
            </div>
          )}

          {!loading && !error && visibleStores.length > 0 && (
            <div className="directory-grid">
              {visibleStores.map((store) => {
                const href = tenantUrl(store.storeSlug);
                return (
                  <article className="directory-card" key={store.storeSlug}>
                    <div className="directory-card-logo"><DirectoryStoreLogo store={store} /></div>
                    <div className="directory-card-body">
                      <h3>{store.storeName}</h3>
                      {store.address && <p><MapPin /> <span>{store.address}</span></p>}
                      <div className="directory-services">
                        {store.deliveryEnabled && <span>Delivery</span>}
                        {store.pickupEnabled && <span>Pickup</span>}
                      </div>
                    </div>
                    {href && <a className="directory-visit" href={href}>Visit Store <ArrowRight /></a>}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <footer className="directory-footer">
        <span>Online storefronts powered by Elmor POS</span>
        <span>Elmor Technologies</span>
      </footer>
    </div>
  );
}
